import React, { useState, useEffect } from "react";
import TopNavigation from "components/ui/TopNavigation";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import KPICard from "./components/KPICard";
import RevenueMemberChart from "./components/RevenueMemberChart";
import PerformanceAlerts from "./components/PerformanceAlerts";
import ExpiringMemberships from "./components/ExpiringMemberships";
import PaymentPendingSummary from "./components/PaymentPendingSummary";
import BranchComparisonTable from "./components/BranchComparisonTable";
import QuickStatsRow from "./components/QuickStatsRow";

const kpiData = [
  {
    title: "Total Revenue (MTD)",
    value: "76,400",
    change: "+12.4%",
    changeType: "positive",
    icon: "DollarSign",
    iconColor: "var(--color-primary)",
    prefix: "$",
  },
  {
    title: "Active Members",
    value: "1,222",
    change: "+5.8%",
    changeType: "positive",
    icon: "Users",
    iconColor: "var(--color-secondary)",
  },
  {
    title: "Retention Rate",
    value: "84.2",
    change: "+1.3%",
    changeType: "positive",
    icon: "RefreshCw",
    iconColor: "var(--color-success)",
    suffix: "%",
  },
  {
    title: "Monthly Growth",
    value: "8.7",
    change: "-0.4%",
    changeType: "negative",
    icon: "TrendingUp",
    iconColor: "var(--color-accent)",
    suffix: "%",
  },
];

export default function ExecutiveOverview() {
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [liveRevenue, setLiveRevenue] = useState(76400);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveRevenue(prev => prev + Math.floor(Math.random() * 120 - 20));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastRefresh(new Date());
      setIsRefreshing(false);
    }, 1500);
  };

  const formatTime = (date) => date?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 md:py-6 lg:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
              Executive Overview
            </h1>
            <p className="text-xs md:text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
              Strategic business intelligence across all branches · Last updated {formatTime(lastRefresh)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-success)]/10 rounded-full">
              <span className="w-2 h-2 rounded-full bg-[var(--color-success)] animate-pulse" />
              <span className="text-xs font-medium text-[var(--color-success)] font-[var(--font-caption)]">Live Data</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName={isRefreshing ? "Loader2" : "RefreshCw"}
              iconPosition="left"
              iconSize={14}
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </Button>
            <Button variant="default" size="sm" iconName="FileDown" iconPosition="left" iconSize={14}>
              Export Report
            </Button>
          </div>
        </div>

        {/* KPI Cards Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-5 md:mb-6">
          {kpiData?.map((kpi, i) => (
            <KPICard key={i} {...kpi} value={i === 0 ? liveRevenue?.toLocaleString() : kpi?.value} />
          ))}
        </div>

        {/* Main Content: Chart + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 mb-5 md:mb-6">
          {/* Main Chart - 8 cols */}
          <div className="lg:col-span-8">
            <RevenueMemberChart />
          </div>

          {/* Right Sidebar - 4 cols */}
          <div className="lg:col-span-4 flex flex-col gap-4 md:gap-5">
            <PerformanceAlerts />
          </div>
        </div>

        {/* Second Row: Expiring + Payment Pending + Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 mb-5 md:mb-6">
          <div className="lg:col-span-4">
            <ExpiringMemberships />
          </div>
          <div className="lg:col-span-4">
            <PaymentPendingSummary />
          </div>
          <div className="lg:col-span-4 flex flex-col gap-4">
            {/* Mini KPI Summary */}
            <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 shadow-[var(--shadow-sm)]">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-3">Today's Snapshot</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Check-ins", value: "247", icon: "LogIn", color: "var(--color-primary)" },
                  { label: "New Leads", value: "18", icon: "UserPlus", color: "var(--color-success)" },
                  { label: "Renewals", value: "12", icon: "RefreshCw", color: "var(--color-secondary)" },
                  { label: "Invoices", value: "34", icon: "FileText", color: "var(--color-accent)" },
                ]?.map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-2.5 bg-[var(--color-muted)]/50 rounded-[var(--radius-sm)]">
                    <div className="w-8 h-8 rounded-[var(--radius-sm)] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item?.color}18` }}>
                      <Icon name={item?.icon} size={15} color={item?.color} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-data)] leading-none">{item?.value}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{item?.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Attendance Heatmap Mini */}
            <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 shadow-[var(--shadow-sm)] flex-1">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-3">Peak Hours Today</h3>
              <div className="flex items-end gap-1 h-16">
                {[12, 18, 24, 31, 45, 62, 78, 89, 95, 87, 72, 58, 44, 38, 52, 67, 81, 76, 61, 43, 28, 19, 12, 8]?.map((v, i) => (
                  <div key={i} className="flex-1 rounded-sm transition-all" style={{
                    height: `${v}%`,
                    backgroundColor: v > 70 ? "var(--color-primary)" : v > 40 ? "var(--color-secondary)" : "var(--color-muted)",
                    opacity: 0.85
                  }} title={`${i}:00 - ${v} check-ins`} />
                ))}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">12am</span>
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">12pm</span>
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">11pm</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="mb-5 md:mb-6">
          <QuickStatsRow />
        </div>

        {/* Branch Comparison Table */}
        <div className="mb-5 md:mb-6">
          <BranchComparisonTable />
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 border-t border-[var(--color-border)]">
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            © {new Date()?.getFullYear()} myfitnesstracking Analytics · All data refreshes every 30 minutes
          </p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Data as of {lastRefresh?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            <div className="flex items-center gap-1.5">
              <Icon name="Shield" size={12} color="var(--color-success)" />
              <span className="text-xs text-[var(--color-success)] font-[var(--font-caption)]">SSL Secured</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}